import * as React from "react";
import {
  createStyles,
  Theme,
  makeStyles,
  IconButton,
  Fab,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  InputAdornment,
  ListItem,
  List,
} from "@material-ui/core";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SubdirectoryArrowRight as TreeIcon,
} from "@material-ui/icons";
import Loader from "../components/Loader";
import { AnvokError, String } from "lib";
import AutoCompleteInput from "../components/AutoCompleteInput";
import CardFlex from "screens/components/CardFlex";
import { useErrorHandler } from "../../ErrorHandler";
import { useOrg, useApiClient } from "lib/store";
import { ApiOrgConfigCarrier } from "lib/api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttons: { textAlign: "right" },
    icon: {
      fontSize: 18,
      verticalAlign: "text-bottom",
      color: "rgb(100,100,100)",
    },
    content: {
      padding: 0,
    },
    editCell: {
      flexGrow: 1,
      textAlign: "right",
    },
    dialogPaper: {
      minWidth: 600,
    },
  })
);

interface Props {
  className?: string;
}

const Carriers: React.FC<Props> = ({ className }) => {
  const classes = useStyles();
  const org = useOrg();
  const api = useApiClient();
  const errorHandler = useErrorHandler();
  const [dialog, setDialog] = React.useState(false);
  const [key, setKey] = React.useState<string | undefined>(undefined);
  const [label, setLabel] = React.useState("");
  const [matches, setMatches] = React.useState<string[]>([""]);
  const [loading, setLoading] = React.useState(false);
  const [livraisonModes, setLivraisonModes] = React.useState<
    string[] | undefined
  >(undefined);

  const carriers = org.config.carriers;

  const onMatchChange = React.useCallback((value: string, index: number) => {
    setMatches((current) => {
      const newMatches = [...current];
      newMatches[index] = value;
      return newMatches;
    });
  }, []);

  const extractConfig = React.useCallback((): ApiOrgConfigCarrier => {
    return {
      key: key
        ? key
        : String.uniqKey(
            label,
            org.config.carriers.map((v) => v.key)
          ),
      label: label,
      matches: matches,
    };
  }, [key, label, matches, org]);

  const deleteCarrier = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    const config = extractConfig();
    await api.DELETE("/api/orgs/{org}/carriers/{key}", {
      params: { path: { org: org.org, key: config.key } },
    });
  }, [org, api, extractConfig]);

  const saveCarrier = React.useCallback(async (): Promise<void> => {
    setLoading(true);
    const config = extractConfig();
    if (key) {
      await api.PUT("/api/orgs/{org}/carriers/{key}", {
        params: { path: { org: org.org, key: config.key } },
        body: config,
      });
    } else {
      await api.POST("/api/orgs/{org}/carriers", {
        params: { path: { org: org.org } },
        body: config,
      });
    }
  }, [org, api, key, extractConfig]);

  const openDialog = React.useCallback(
    (i?: number): void => {
      setDialog(true);
      setKey(undefined);
      setLabel("");
      setMatches([]);
      setLoading(false);

      if (i !== undefined) {
        setKey(org.config.carriers[i].key);
        setLabel(org.config.carriers[i].label);
        setMatches([...org.config.carriers[i].matches]);
      }

      if (!livraisonModes) {
        setLoading(true);
        api
          .GET("/api/orders/shippings")
          .then((r) => {
            if (r.error) {
              throw new AnvokError(r.error.message);
            }
            setLivraisonModes(r.data.values);
          })
          .catch(errorHandler)
          .finally(() => setLoading(false));
      }
    },
    [livraisonModes, org, api, errorHandler]
  );

  const closeDialog = React.useCallback((): void => {
    setDialog(false);
    setLoading(false);
  }, []);

  const removeMatch = React.useCallback((i: number): void => {
    setMatches((current) => {
      const newMatches = [...current];
      newMatches.splice(i, 1);
      return newMatches;
    });
  }, []);

  const addMatch = React.useCallback((): void => {
    setMatches((current) => [...current, ""]);
  }, []);

  const handleLabelChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLabel(e.target.value);
    },
    []
  );

  const actions = (
    <Fab
      color="primary"
      size="small"
      onClick={() => openDialog()}
      aria-label="Save"
    >
      <AddIcon />
    </Fab>
  );

  return (
    <CardFlex
      title="Transports"
      actions={actions}
      contentClass={classes.content}
      loading={loading}
    >
      <List>
        {carriers.map((c, i) => {
          return (
            <ListItem key={c.key}>
              <div>
                <div>{c.label}</div>
                {c.matches.map((v, i) => {
                  return (
                    <div key={i}>
                      <TreeIcon className={classes.icon} /> {v}
                    </div>
                  );
                })}
              </div>
              <div className={classes.editCell}>
                <IconButton onClick={() => openDialog(i)}>
                  <EditIcon />
                </IconButton>
              </div>
            </ListItem>
          );
        })}
      </List>
      <Dialog
        open={dialog}
        onClose={closeDialog}
        aria-labelledby="carriers-form-dialog-title"
        classes={{ paper: classes.dialogPaper }}
      >
        <Loader show={loading} message="Veuillez patienter...">
          <DialogTitle id="carriers-form-dialog-title">
            Ajout transport
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              value={label}
              name="label"
              label="Intitulé"
              type="text"
              onChange={handleLabelChange}
              fullWidth
            />
            <Typography variant="subtitle1" component="h3">
              Correspondances
            </Typography>
            {matches.map((v, i) => {
              return (
                <AutoCompleteInput
                  options={livraisonModes}
                  key={i}
                  name="matches"
                  value={v}
                  onChange={(value) => onMatchChange(value, i)}
                  fullWidth
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="Retirer correspondance"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMatch(i);
                        }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </InputAdornment>
                  }
                />
              );
            })}
            <div className={classes.buttons}>
              <IconButton
                aria-label="Ajouter correspondance"
                onClick={addMatch}
              >
                <AddIcon />
              </IconButton>
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={deleteCarrier}
              style={{ visibility: key ? "visible" : "hidden" }}
              color="default"
            >
              Supprimer
            </Button>
            <Button onClick={closeDialog} color="primary">
              Annuler
            </Button>
            <Button onClick={saveCarrier} color="primary">
              Enregistrer
            </Button>
          </DialogActions>
        </Loader>
      </Dialog>
    </CardFlex>
  );
};

export default Carriers;
